import { useEffect, useRef } from 'react';
import { chatClient } from '@/services/chat-client';
import { useChatStore } from '@/stores/chat-store';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ChatMessage } from './chat-message';
import { ChatRoomInput } from './chat-room-input';
import { Plus } from 'lucide-react';

const AI_ROOM_NAME = 'AI Chat';

/** Room-based chat panel — excludes the AI room, which lives in the AI tab */
export function ChatPanel(): React.JSX.Element {
  const rooms = useChatStore((s) => s.rooms);
  const messages = useChatStore((s) => s.messages);
  const activeRoomId = useChatStore((s) => s.activeRoomId);
  const setActiveRoomId = useChatStore((s) => s.setActiveRoomId);
  const loadMessages = useChatStore((s) => s.loadMessages);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const chatRooms = rooms.filter((r) => r.name !== AI_ROOM_NAME);

  useEffect(() => {
    const isAiRoom = rooms.find((r) => r.id === activeRoomId)?.name === AI_ROOM_NAME;
    if (chatRooms.length > 0 && (!activeRoomId || isAiRoom)) {
      const firstId = chatRooms[0]!.id;
      setActiveRoomId(firstId);
      loadMessages(firstId);
    }
  }, [chatRooms, activeRoomId, setActiveRoomId, loadMessages, rooms]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleRoomChange(roomId: string): Promise<void> {
    setActiveRoomId(roomId);
    await loadMessages(roomId);
  }

  async function handleCreateRoom(): Promise<void> {
    const name = window.prompt('Room name:');
    if (!name?.trim()) return;
    const room = await chatClient.createRoom(name.trim());
    if (room) {
      await useChatStore.getState().loadRooms();
      setActiveRoomId(room.id);
      await loadMessages(room.id);
    }
  }

  if (chatRooms.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
        <p className="text-sm text-muted-foreground">No chat rooms yet</p>
        <Button size="sm" variant="outline" onClick={handleCreateRoom}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Create Room
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden p-4">
      <div className="flex items-center gap-2 pb-3">
        <select
          className="flex-1 rounded-md border border-echo-border bg-white/[0.04] px-3 py-1.5 text-sm text-foreground outline-none transition-colors hover:bg-white/[0.06] focus:border-echo-yellow"
          value={activeRoomId}
          onChange={(e) => handleRoomChange(e.target.value)}
        >
          {chatRooms.map((room) => (
            <option key={room.id} value={room.id}>{room.name}</option>
          ))}
        </select>
        <Button size="icon" variant="outline" onClick={handleCreateRoom} title="New room">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      <ScrollArea className="flex-1 space-y-1.5">
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}
        <div ref={messagesEndRef} />
      </ScrollArea>

      <ChatRoomInput roomId={activeRoomId} />
    </div>
  );
}
